export interface ProcessedSignature {
  dataUrl: string
  width: number
  height: number
}

export async function processSignatureImage(
  imageSource: string | File,
  options: {
    maxWidth?: number
    maxHeight?: number
    luminanceThreshold?: number
  } = {}
): Promise<ProcessedSignature> {
  const {
    maxWidth = 400,
    maxHeight = 150,
    luminanceThreshold = 200,
  } = options

  const img = await loadImage(imageSource)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!

  let { width, height } = img
  const aspectRatio = width / height

  if (width > maxWidth) {
    width = maxWidth
    height = width / aspectRatio
  }
  if (height > maxHeight) {
    height = maxHeight
    width = height * aspectRatio
  }

  canvas.width = width
  canvas.height = height

  ctx.drawImage(img, 0, 0, width, height)

  const imageData = ctx.getImageData(0, 0, width, height)
  const data = imageData.data

  removeBackground(data, luminanceThreshold)

  const bounds = findSignatureBounds(data, width, height)
  
  if (bounds) {
    const croppedWidth = bounds.maxX - bounds.minX + 20
    const croppedHeight = bounds.maxY - bounds.minY + 20
    
    const croppedCanvas = document.createElement('canvas')
    croppedCanvas.width = croppedWidth
    croppedCanvas.height = croppedHeight
    const croppedCtx = croppedCanvas.getContext('2d')!
    
    ctx.putImageData(imageData, 0, 0)
    croppedCtx.drawImage(
      canvas,
      bounds.minX - 10,
      bounds.minY - 10,
      croppedWidth,
      croppedHeight,
      0,
      0,
      croppedWidth,
      croppedHeight
    )

    return {
      dataUrl: croppedCanvas.toDataURL('image/png'),
      width: croppedWidth,
      height: croppedHeight,
    }
  }

  ctx.putImageData(imageData, 0, 0)

  return {
    dataUrl: canvas.toDataURL('image/png'),
    width,
    height,
  }
}

function loadImage(source: string | File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject

    if (typeof source === 'string') {
      img.src = source
    } else {
      const reader = new FileReader()
      reader.onload = (e) => {
        img.src = e.target?.result as string
      }
      reader.onerror = reject
      reader.readAsDataURL(source)
    }
  })
}

function removeBackground(data: Uint8ClampedArray, threshold: number): void {
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i]
    const g = data[i + 1]
    const b = data[i + 2]

    const luminance = 0.299 * r + 0.587 * g + 0.114 * b

    if (luminance > threshold) {
      data[i + 3] = 0
    } else {
      const darkness = 1 - luminance / threshold
      data[i] = Math.round(data[i] * darkness)
      data[i + 1] = Math.round(data[i + 1] * darkness)
      data[i + 2] = Math.round(data[i + 2] * darkness)
      data[i + 3] = Math.round(255 * Math.min(1, darkness * 1.5))
    }
  }
}

function findSignatureBounds(
  data: Uint8ClampedArray,
  width: number,
  height: number
): { minX: number; minY: number; maxX: number; maxY: number } | null {
  let minX = width
  let minY = height
  let maxX = 0
  let maxY = 0
  let hasContent = false

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4
      const alpha = data[i + 3]

      if (alpha > 20) {
        hasContent = true
        if (x < minX) minX = x
        if (x > maxX) maxX = x
        if (y < minY) minY = y
        if (y > maxY) maxY = y
      }
    }
  }

  if (!hasContent) return null

  return { minX, minY, maxX, maxY }
}

export function applyAdaptiveThreshold(
  imageData: ImageData,
  blockSize: number = 15,
  c: number = 10
): void {
  const { data, width, height } = imageData
  const grayscale = new Uint8Array(width * height)

  for (let i = 0; i < data.length; i += 4) {
    const idx = i / 4
    grayscale[idx] = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2])
  }

  const halfBlock = Math.floor(blockSize / 2)

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let sum = 0
      let count = 0

      for (let dy = -halfBlock; dy <= halfBlock; dy++) {
        for (let dx = -halfBlock; dx <= halfBlock; dx++) {
          const ny = y + dy
          const nx = x + dx
          if (ny >= 0 && ny < height && nx >= 0 && nx < width) {
            sum += grayscale[ny * width + nx]
            count++
          }
        }
      }

      const threshold = sum / count - c
      const idx = y * width + x
      const pixelIdx = idx * 4

      if (grayscale[idx] > threshold) {
        data[pixelIdx + 3] = 0
      } else {
        const darkness = 1 - grayscale[idx] / 255
        data[pixelIdx] = 0
        data[pixelIdx + 1] = 0
        data[pixelIdx + 2] = 0
        data[pixelIdx + 3] = Math.round(255 * Math.min(1, darkness * 2))
      }
    }
  }
}
